/**
 * Чистая логика приглашений: состояние выданной ссылки, разбор `?invite=` и
 * сборка ссылки. Всё, что можно проверить без DOM — здесь; компоненты только
 * рисуют результат.
 */

export type InviteState = 'active' | 'used' | 'expired';

export const INVITE_STATE_LABEL: Record<InviteState, string> = {
  active: 'активна',
  used: 'использована',
  expired: 'истекла',
};

/** Имя query-параметра ссылки-приглашения. */
export const INVITE_PARAM = 'invite';

/**
 * Состояние приглашения на момент `now`.
 *
 * Погашение важнее истечения: ссылку уже потратили, и «истекла» тут врало бы о
 * причине. Нечитаемая `expires_at` не считается истечением — судит всё равно
 * сервер при погашении, а UI не должен объявлять живую ссылку мёртвой из-за
 * неожиданного формата даты.
 */
export function inviteState(
  invite: { used_at: string | null; expires_at: string },
  now: string | Date,
): InviteState {
  if (invite.used_at) return 'used';
  const expiresMs = Date.parse(invite.expires_at);
  if (Number.isNaN(expiresMs)) return 'active';
  const nowMs = typeof now === 'string' ? Date.parse(now) : now.getTime();
  if (Number.isNaN(nowMs)) return 'active';
  return expiresMs <= nowMs ? 'expired' : 'active';
}

/**
 * Токен из query-строки (`?invite=…`). Пустое/пробельное значение — это не
 * токен, а мусор из ручной правки адреса: возвращаем null, чтобы форма
 * регистрации не считала себя «по приглашению».
 */
export function readInviteToken(search: string): string | null {
  const raw = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(
    INVITE_PARAM,
  );
  const token = raw?.trim() ?? '';
  return token === '' ? null : token;
}

/** Ссылка, которую копируют приглашающему: открывает приложение с токеном. */
export function inviteLink(token: string, origin: string): string {
  return `${origin.replace(/\/+$/, '')}/?${INVITE_PARAM}=${encodeURIComponent(token)}`;
}

/**
 * Та же query-строка без `invite` — после успешной регистрации токен погашен, и
 * оставлять его в адресе значит при перезагрузке снова показать форму «по
 * приглашению» с уже мёртвым токеном. Пустой результат — пустая строка, а не
 * голый «?».
 */
export function withoutInviteParam(search: string): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  params.delete(INVITE_PARAM);
  const rest = params.toString();
  return rest === '' ? '' : `?${rest}`;
}
